import React, { useState, useEffect } from 'react';
import './QuantityInput.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamation, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';
import useDebounce from './../../libraries/hooks/useDebounce';

function QuantityInput(props) {
	const {
		t
	} = props;

	const reduxQuantity = props.quantity;
	const [internalQuantity, setInternalQuantity] = useState(reduxQuantity || 0);
	const debouncedInternalQuantity = useDebounce(internalQuantity, 300);
	const quantityInput = React.createRef();
	const { onQuantityChange } = props;

	useEffect(() => {
		setInternalQuantity(reduxQuantity);
	}, [reduxQuantity]);

	useEffect(() => {
		if (typeof onQuantityChange === 'function') {
			// Don't update if redux and local state already have the same value
			// undefined should be handled the same as 0 or null => convert values to bools
			// console.log({ reduxQuantity, internalQuantity, debouncedInternalQuantity });
			if (isQuantityConsistent(reduxQuantity, debouncedInternalQuantity)) return;
			onQuantityChange(debouncedInternalQuantity);
		}
	}, [debouncedInternalQuantity, onQuantityChange, reduxQuantity]);

	const focusQuantityInput = (e) => {
		e.stopPropagation();
		// @ts-ignore
		quantityInput.current.focus();
	};

	const increment = () => {
		const quantity = internalQuantity === undefined ? 0 : internalQuantity;
		handleQuantityChange(quantity + 1);
	};

	const decrement = () => {
		const quantity = internalQuantity === undefined ? 0 : internalQuantity;
		handleQuantityChange(quantity - 1);
	};

	const handleQuantityChange = input => {
		let quantity = input;
		if (typeof input === 'object') quantity = input.target.value === '' ? 0 : parseInt(input.target.value);
		if (quantity === null) quantity = 0;
		if (Number.isNaN(quantity)) quantity = internalQuantity;
		if (quantity < 0) quantity = 0;

		setInternalQuantity(quantity);
	};

	const isQuantityConsistent = (reduxQuantity, internalQuantity) => {
		const reduxQ = reduxQuantity ?? 0;
		const internalQ = internalQuantity ?? 0;
		return reduxQ === internalQ;
	};

	const {
		min,
		max,
		awardedPromo,
		disabled,
		hidden,
		orderAmount,
		madQuantityIndicator,
		focus,
		size
	} = props;



	return (
		<div
			className={`
				QuantityInput
				${isQuantityConsistent(reduxQuantity, internalQuantity) ? 'consistent' : 'inconsistent'}
				${size ? size : ''}
				${madQuantityIndicator ? 'mq' : ''}
				${focus ? 'focus' : ''}
			`}
			onClick={(e) => e.stopPropagation()}
		>
			<span className={`quantity__button button--decrement ${disabled ? 'disabled' : ''} ${hidden ? 'hidden' : ''}`} onClick={decrement}>
				<FontAwesomeIcon icon={faMinus} />
			</span>
			<input
				className={`quantity__input input--length-${(internalQuantity || 1).toString().length}`}
				type="number"
				min={min || 0}
				max={max || ''}
				placeholder="0"
				onChange={handleQuantityChange}
				value={internalQuantity || ''}
				// @ts-ignore
				ref={quantityInput}
				disabled={disabled}
			/>

			{awardedPromo && (
				<span onClick={focusQuantityInput} className={'quantity__promo-quantity'} data-free={t('order.label_free')}>
					{awardedPromo}
				</span>
			)}

			{orderAmount && orderAmount !== 1 && (
				<span onClick={focusQuantityInput} className={'quantity__order-amount'}>
					<span className={'multiplication-sign'}>×</span>{orderAmount}
				</span>
			)}

			{madQuantityIndicator && (
				<div onClick={focusQuantityInput} className={'mad-quantity'}>
					<span className={'mad-quantity__indicator'}>
						<FontAwesomeIcon icon={faExclamation} />
					</span>
				</div>
			)}

			<span onClick={focusQuantityInput} className={'quantity__unit'}>
				{t('order.productUnitPlural')}
			</span>

			<span className={`quantity__button button--increment ${disabled ? 'disabled' : ''} ${hidden ? 'hidden' : ''}`} onClick={increment}>
				<FontAwesomeIcon icon={faPlus} />
			</span>
		</div>
	);
}

QuantityInput.propTypes = {
	quantity: PropTypes.number,
	awardedPromo: PropTypes.number,
	onQuantityChange: PropTypes.func,
	disabled: PropTypes.bool,
	orderAmount: PropTypes.number,
	madQuantityIndicator: PropTypes.bool,
	focus: PropTypes.bool,
	size: PropTypes.oneOf(['compact', 'big', '']),
	debounce: PropTypes.number,
	t: PropTypes.func
};

export default QuantityInput;
